import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface BpSection {
  id: string;
  title: string;
  description: string;
}

interface FinancialRow {
  label: string;
  y1: number;
  y2: number;
  y3: number;
}

const PRIMARY_COLOR: [number, number, number] = [217, 165, 60]; // amber/gold
const DARK_COLOR: [number, number, number] = [30, 30, 35];
const MUTED_COLOR: [number, number, number] = [120, 120, 130];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);
}

export function generateBusinessPlanPdf(
  sections: Record<string, string>,
  bpSections: BpSection[],
  financialRows: FinancialRow[],
  evaluationCriteria: { label: string; score: number }[]
): Blob {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = 0;

  // ===== COVER PAGE =====
  doc.setFillColor(...DARK_COLOR);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  // Accent line
  doc.setFillColor(...PRIMARY_COLOR);
  doc.rect(margin, 60, 40, 3, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(36);
  doc.setFont("helvetica", "bold");
  doc.text("Business Plan", margin, 80);

  doc.setFontSize(14);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text("Généré par Grafton IA", margin, 92);

  doc.setFontSize(10);
  doc.setTextColor(180, 180, 180);
  doc.text(`Date : ${new Date().toLocaleDateString("fr-FR")}`, margin, 108);

  // Table of contents on cover
  doc.setFontSize(11);
  doc.setTextColor(200, 200, 200);
  let tocY = 140;
  bpSections.forEach((s, i) => {
    const filled = (sections[s.id] || "").trim().length > 20;
    doc.setTextColor(filled ? 255 : 120, filled ? 255 : 120, filled ? 255 : 120);
    doc.text(`${i + 1}. ${s.title}`, margin + 4, tocY);
    if (filled) {
      doc.setFillColor(...PRIMARY_COLOR);
      doc.circle(margin, tocY - 1.2, 1.2, "F");
    } else {
      doc.setDrawColor(100, 100, 100);
      doc.circle(margin, tocY - 1.2, 1.2, "S");
    }
    tocY += 8;
  });

  // ===== CONTENT PAGES =====
  bpSections.forEach((section, index) => {
    doc.addPage();
    y = margin;

    // Section header bar
    doc.setFillColor(...PRIMARY_COLOR);
    doc.rect(0, 0, pageWidth, 8, "F");

    // Section number + title
    y = 28;
    doc.setFontSize(10);
    doc.setTextColor(...PRIMARY_COLOR);
    doc.setFont("helvetica", "bold");
    doc.text(`SECTION ${index + 1}`, margin, y);

    y += 8;
    doc.setFontSize(20);
    doc.setTextColor(...DARK_COLOR);
    doc.text(section.title, margin, y);

    y += 6;
    doc.setFontSize(9);
    doc.setTextColor(...MUTED_COLOR);
    doc.setFont("helvetica", "italic");
    doc.text(section.description, margin, y);

    y += 10;
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;

    // Content
    const content = (sections[section.id] || "").trim();
    if (content) {
      doc.setFontSize(11);
      doc.setTextColor(50, 50, 55);
      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(content, contentWidth);
      for (const line of lines) {
        if (y > pageHeight - 20) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, margin, y);
        y += 5.5;
      }
    } else {
      doc.setFontSize(11);
      doc.setTextColor(170, 170, 170);
      doc.setFont("helvetica", "italic");
      doc.text("Section non remplie", margin, y);
    }
  });

  // ===== FINANCIAL PAGE =====
  doc.addPage();
  doc.setFillColor(...PRIMARY_COLOR);
  doc.rect(0, 0, pageWidth, 8, "F");

  y = 28;
  doc.setFontSize(10);
  doc.setTextColor(...PRIMARY_COLOR);
  doc.setFont("helvetica", "bold");
  doc.text("PRÉVISIONNEL", margin, y);
  y += 8;
  doc.setFontSize(20);
  doc.setTextColor(...DARK_COLOR);
  doc.text("Tableau Financier sur 3 ans", margin, y);
  y += 12;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [["Poste", "Année 1", "Année 2", "Année 3"]],
    body: financialRows.map((r) => [
      r.label,
      formatCurrency(r.y1),
      formatCurrency(r.y2),
      formatCurrency(r.y3),
    ]),
    headStyles: {
      fillColor: DARK_COLOR,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
    },
    bodyStyles: { fontSize: 9, textColor: [50, 50, 55] },
    alternateRowStyles: { fillColor: [245, 245, 248] },
    styles: { cellPadding: 3 },
    didParseCell: (data) => {
      if (data.section === "body") {
        const label = financialRows[data.row.index]?.label || "";
        if (label.includes("Résultat") || label.includes("Marge")) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [240, 235, 220];
        }
        if (data.column.index > 0) {
          const val = financialRows[data.row.index]?.[["y1", "y2", "y3"][data.column.index - 1] as "y1" | "y2" | "y3"];
          if (val !== undefined && val < 0) {
            data.cell.styles.textColor = [200, 60, 60];
          }
        }
      }
    },
  });

  // ===== EVALUATION PAGE =====
  doc.addPage();
  doc.setFillColor(...PRIMARY_COLOR);
  doc.rect(0, 0, pageWidth, 8, "F");

  y = 28;
  doc.setFontSize(10);
  doc.setTextColor(...PRIMARY_COLOR);
  doc.setFont("helvetica", "bold");
  doc.text("ÉVALUATION", margin, y);
  y += 8;
  doc.setFontSize(20);
  doc.setTextColor(...DARK_COLOR);
  doc.text("Scoring du Projet", margin, y);
  y += 14;

  const globalScore = Math.round(evaluationCriteria.reduce((a, c) => a + c.score, 0) / evaluationCriteria.length);

  evaluationCriteria.forEach((c) => {
    doc.setFontSize(10);
    doc.setTextColor(...DARK_COLOR);
    doc.setFont("helvetica", "normal");
    doc.text(c.label, margin, y);
    doc.setFont("helvetica", "bold");
    doc.text(`${c.score}/100`, pageWidth - margin - 15, y, { align: "right" });

    // Progress bar background
    const barX = margin;
    const barY = y + 2;
    const barWidth = contentWidth - 20;
    const barHeight = 4;
    doc.setFillColor(230, 230, 235);
    doc.roundedRect(barX, barY, barWidth, barHeight, 2, 2, "F");

    // Progress bar fill
    const fillColor: [number, number, number] = c.score >= 75 ? [60, 180, 100] : c.score >= 60 ? [220, 180, 50] : [220, 120, 50];
    doc.setFillColor(...fillColor);
    doc.roundedRect(barX, barY, barWidth * (c.score / 100), barHeight, 2, 2, "F");

    y += 14;
  });

  // Global score
  y += 6;
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, pageWidth - margin, y);
  y += 10;
  doc.setFontSize(14);
  doc.setTextColor(...DARK_COLOR);
  doc.setFont("helvetica", "bold");
  doc.text("Score global", margin, y);
  doc.setFontSize(24);
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text(`${globalScore}/100`, pageWidth - margin, y, { align: "right" });

  // Footer on every page
  const totalPages = doc.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(170, 170, 170);
    doc.text(`Grafton IA — Business Plan`, margin, pageHeight - 8);
    doc.text(`${i - 1} / ${totalPages - 1}`, pageWidth - margin, pageHeight - 8, { align: "right" });
  }

  return doc.output("blob");
}
